import type { PaginatedQuery, ThreadListQuery } from '@atlas/api-contract';

/**
 * The single source of truth for React Query cache keys.
 *
 * Keys are hierarchical and every level is addressable, so an invalidation can
 * be aimed as precisely or as broadly as the mutation warrants: creating a
 * prototype invalidates `queryKeys.prototypes.lists(projectId)` and leaves every
 * detail entry alone, while deleting a project invalidates
 * `queryKeys.projects.all()` and takes the whole subtree with it.
 *
 * Two rules keep that working:
 *
 * - every key starts with the same root, so `queryClient.removeQueries()` with
 *   no filter is never needed;
 * - a list key always carries its query object *last*, so the key prefix
 *   without it matches every page, sort and search of that list.
 *
 * Nothing outside this module may write a key literal.
 */

const ROOT = ['atlas'] as const;

export const queryKeys = {
  /** Every Atlas query. The invalidation of last resort. */
  all: () => ROOT,

  auth: {
    /** The current session, from `GET /v1/auth/me`. */
    session: () => [...ROOT, 'auth', 'session'] as const,
  },

  users: {
    /** Every user query — the admin roster and its pages. */
    all: () => [...ROOT, 'users'] as const,
    /** Every page/search of the admin user list. */
    lists: () => [...queryKeys.users.all(), 'list'] as const,
    /** One page of the admin user list. */
    list: (query: PaginatedQuery) =>
      [...queryKeys.users.lists(), query] as const,
  },

  projects: {
    /** Every project query — lists and details alike. */
    all: () => [...ROOT, 'projects'] as const,
    /** Every page/sort/search of the project list. */
    lists: () => [...queryKeys.projects.all(), 'list'] as const,
    /** One page of the project list. */
    list: (query: PaginatedQuery) =>
      [...queryKeys.projects.lists(), query] as const,
    /** Every project detail. */
    details: () => [...queryKeys.projects.all(), 'detail'] as const,
    /** One project. */
    detail: (projectId: string) =>
      [...queryKeys.projects.details(), projectId] as const,
  },

  prototypes: {
    all: () => [...ROOT, 'prototypes'] as const,
    /** Every page of the prototype list inside one project. */
    lists: (projectId: string) =>
      [...queryKeys.prototypes.all(), 'list', projectId] as const,
    list: (projectId: string, query: PaginatedQuery) =>
      [...queryKeys.prototypes.lists(projectId), query] as const,
    details: () => [...queryKeys.prototypes.all(), 'detail'] as const,
    detail: (prototypeId: string) =>
      [...queryKeys.prototypes.details(), prototypeId] as const,
  },

  versions: {
    all: () => [...ROOT, 'versions'] as const,
    /** Every version of one prototype, newest first. */
    list: (prototypeId: string) =>
      [...queryKeys.versions.all(), 'list', prototypeId] as const,
    details: () => [...queryKeys.versions.all(), 'detail'] as const,
    detail: (versionId: string) =>
      [...queryKeys.versions.details(), versionId] as const,
  },

  threads: {
    all: () => [...ROOT, 'threads'] as const,
    /** Every filter combination of the thread panel for one version. */
    lists: (versionId: string) =>
      [...queryKeys.threads.all(), 'list', versionId] as const,
    list: (versionId: string, query: ThreadListQuery) =>
      [...queryKeys.threads.lists(versionId), query] as const,
    details: () => [...queryKeys.threads.all(), 'detail'] as const,
    detail: (threadId: string) =>
      [...queryKeys.threads.details(), threadId] as const,
  },

  comments: {
    all: () => [...ROOT, 'comments'] as const,
    /** Every comment on one thread. */
    list: (threadId: string) =>
      [...queryKeys.comments.all(), 'list', threadId] as const,
  },
} as const;

/** The type of any key this factory produces. */
export type QueryKey = ReturnType<
  | typeof queryKeys.all
  | typeof queryKeys.projects.list
  | typeof queryKeys.projects.detail
  | typeof queryKeys.prototypes.list
  | typeof queryKeys.prototypes.detail
  | typeof queryKeys.versions.list
  | typeof queryKeys.versions.detail
  | typeof queryKeys.threads.list
  | typeof queryKeys.threads.detail
  | typeof queryKeys.comments.list
>;
